import api from '@/api/api.js'
import ChoixPrestations from '@/components/choix-prestations'
import store_patient from '@/store/mods/clinique/patients'
import store_prestation from '@/store/mods/clinique/prestations'

const normaliser = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
const liste = value => Array.isArray(value) ? value : []
const preparer = value => liste(value).map(item => Object.assign({}, item, { recherche_normalisee: normaliser(item.nom) }))

export default {
    name: 'dropdown-quick-actions',
    components: { ChoixPrestations },
    data() {
        return {
            onglet: 'consultations',
            analyse_vue: 'individuelles',
            liste_bilans: [],
            bilans_loading: false,
            bilans_charges: false,
            recherche: '',
            tout_voir: { consultations: false, analyses: false, examens: false },
            chargement: false,
            actualisation_catalogue: false,
            chargement_medecins: false,
            catalogue_charge_a: 0,
            erreur_chargement: false,
            list_type_consultations: [],
            list_type_analyses: [],
            list_examens: [],
            medecins_par_consultation: {},
            medecins_disponibles: true,
            ordres: [],
            reorganiser: false,
            ordre_edition: [],
            enregistrement_ordre: false,
            erreur_ordre: '',
            analyse_checked: [],
            examen_checked: []
        }
    },
    computed: {
        quick_actions() { return this.$store.getters.quickActionsDropdown },
        selection_count() { return this.analyse_checked.length + this.examen_checked.length },
        selection_total() {
            return this.analyse_checked.concat(this.examen_checked).reduce((total, acte) => total + Number(acte.prix || 0), 0)
        },
        source_courante() {
            const donnees = { consultations: this.list_type_consultations, analyses: this.list_type_analyses, examens: this.list_examens }
            const positions = new Map(this.ordres.filter(ordre => ordre.categorie === this.onglet)
                .map(ordre => [Number(ordre.prestation_id), Number(ordre.position)]))
            return liste(donnees[this.onglet]).slice().sort((a, b) => {
                const pa = positions.has(Number(a.id)) ? positions.get(Number(a.id)) : Number.MAX_SAFE_INTEGER
                const pb = positions.has(Number(b.id)) ? positions.get(Number(b.id)) : Number.MAX_SAFE_INTEGER
                return pa - pb
            })
        },
        categories_analyses() {
            const groupes = new Map()
            this.source_courante.forEach(item => {
                const id = item.categorie_id || 'autres'
                if (!groupes.has(id)) groupes.set(id, { id, nom: item.categorie_nom || 'Autres analyses', analyses: [] })
                groupes.get(id).analyses.push(item)
            })
            return Array.from(groupes.values()).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
        },
        resultats() {
            const mots = normaliser(this.recherche).split(/\s+/).filter(Boolean)
            if (!mots.length) return this.source_courante
            return this.source_courante.filter(item => mots.every(mot => item.recherche_normalisee.includes(mot)))
        },
        lignes() {
            return this.recherche.trim() || this.tout_voir[this.onglet] ? this.resultats : this.resultats.slice(0, 20)
        }
    },
    watch: {
        analyse_vue(vue) {
            if (vue === 'bilans' && !this.bilans_charges) this.charger_bilans()
        },
        $route() {
            if (this.quick_actions) this.fermer()
        },
        quick_actions(ouvert) {
            if (ouvert) {
                this.recherche = ''
                if (!this.selection_count) this.onglet = 'consultations'
                this.charger_catalogue()
                store_patient.prefetchListeAllPatient().catch(() => {})
            }
        }
    },
    mounted() {
        this.$store.dispatch('setQuickActionsDropdown', false)
    },
    methods: {
        async charger_bilans() {
            if (this.bilans_loading) return
            this.bilans_loading = true
            try {
                const bilans = await store_prestation.getListeBilanSante()
                this.liste_bilans = Array.isArray(bilans) ? bilans : []
                this.bilans_charges = true
            } catch (erreur) {
                this.doWarningMsg('Impossible de charger les bilans.')
            } finally {
                this.bilans_loading = false
            }
        },
        prix_manquant(item) { this.doWarningMsg(`Le prix de ${item.nom} n’est pas configuré`) },
        async charger_catalogue(forcer = false) {
            if (this.actualisation_catalogue || (!forcer && this.catalogue_charge_a && Date.now() - this.catalogue_charge_a < 30000)) return
            // Garder les prestations déjà visibles pendant une actualisation en arrière-plan.
            this.chargement = !this.catalogue_charge_a
            this.actualisation_catalogue = true
            this.erreur_chargement = false
            try {
                const { data } = await api.clinique().get('/bons/catalogue', { params: { sans_medecins: 1 }, timeout: 10000 })
                // Afficher les prestations sans attendre l'annuaire des médecins.
                this.list_type_consultations = preparer(data.consultations)
                this.list_type_analyses = preparer(data.analyses)
                this.list_examens = preparer(data.examens)
                this.ordres = liste(data.ordres)
                this.catalogue_charge_a = Date.now()
                if (!this.medecins_disponibles || !Object.keys(this.medecins_par_consultation).length) this.charger_medecins()
            } catch (erreur) {
                this.erreur_chargement = true
            } finally {
                this.chargement = false
                this.actualisation_catalogue = false
            }
        },
        async charger_medecins() {
            if (this.chargement_medecins) return
            this.chargement_medecins = true
            try {
                const { data } = await api.clinique().get('/bons/catalogue/medecins', { timeout: 4000 })
                this.medecins_par_consultation = data.medecins || {}
                this.medecins_disponibles = data.medecins_disponibles !== false
            } catch (erreur) {
                this.medecins_disponibles = false
            } finally {
                this.chargement_medecins = false
            }
        },
        prix_consultation(item) {
            return item.prix == null ? null : Number(item.prix)
        },
        prix_lisible(prix) {
            if (prix === null || prix === undefined || prix === '' || !Number.isFinite(Number(prix))) return 'Non configuré'
            return `${new Intl.NumberFormat('fr-FR').format(Number(prix))} CDF`
        },
        medecins(item) { return liste(this.medecins_par_consultation[item.id]) },
        ouvrir_reorganisation() {
            this.ordre_edition = this.source_courante.slice()
            this.erreur_ordre = ''
            this.reorganiser = true
        },
        deplacer_prestation(index, direction) {
            const destination = index + direction
            if (destination < 0 || destination >= this.ordre_edition.length) return
            const copie = this.ordre_edition.slice()
            const item = copie.splice(index, 1)[0]
            copie.splice(destination, 0, item)
            this.ordre_edition = copie
        },
        async enregistrer_ordre() {
            if (this.enregistrement_ordre) return
            this.enregistrement_ordre = true
            this.erreur_ordre = ''
            try {
                await api.clinique().put('/bons/catalogue/ordre', {
                    categorie: this.onglet, ids: this.ordre_edition.map(item => item.id)
                })
                this.reorganiser = false
                await this.charger_catalogue(true)
            } catch (erreur) {
                this.erreur_ordre = (erreur.response && erreur.response.data && erreur.response.data.message) || 'Impossible d’enregistrer cet ordre.'
            } finally {
                this.enregistrement_ordre = false
            }
        },
        choisir_consultation(item) {
            this.$store.commit('SET_TYPE_ACTION_SELECTED', 'bon_consultation')
            this.$store.commit('SET_TYPE_CONS_SELECTED', { consult_id: item.id, consult_name: item.nom, consult_abr: item.abreviation })
            this.$store.dispatch('setQuickActionsDropdown', false)
            this.$router.push({ name: 'choice-patient' })
        },
        est_selectionne(categorie, id) {
            const selection = categorie === 'analyses' ? this.analyse_checked : this.examen_checked
            return selection.some(item => Number(item.id) === Number(id))
        },
        basculer_acte(categorie, item) {
            if (!(Number(item.prix) > 0)) {
                this.doWarningMsg(`Le prix de ${item.nom} n’est pas configuré`)
                return
            }
            const selection = categorie === 'analyses' ? this.analyse_checked : this.examen_checked
            const index = selection.findIndex(acte => Number(acte.id) === Number(item.id))
            if (index >= 0) selection.splice(index, 1)
            else selection.push({ id: item.id, nom: item.nom, prix: item.prix })
        },
        validerChoix() {
            if (!this.selection_count) return
            this.$store.commit('SET_TYPE_ACTION_SELECTED', 'bon_medical')
            this.$store.commit('SET_ANALYSES_SELECTED', this.analyse_checked.slice())
            this.$store.commit('SET_EXAMENS_SELECTED', this.examen_checked.slice())
            this.$store.dispatch('setQuickActionsDropdown', false)
            this.$router.push({ name: 'choice-patient' })
        },
        fermer() {
            this.analyse_checked = []
            this.examen_checked = []
            this.$store.dispatch('setQuickActionsDropdown', false)
        },
        doWarningMsg(message) {
            if (this.$toasted) this.$toasted.show(message, { position: 'top-center', duration: 3000, type: 'warning' })
        }
    }
}
